import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING,
} from '../GuideKit';

/* Operations → Salesforce → New License.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screen "New User License". One screen, no
 * records touched other than the Case. The phone and email inputs are the
 * standard flowruntime:phone / flowruntime:email components with no label
 * set in the flow, so they show Salesforce's defaults ("Phone", "Email").
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Salesforce',
    subCategory: 'New License',
    subject: subjectRow('New Salesforce license - Jane Smith', { noAccount: true }),
    lookups: [[
      'Office',
      <>Required. The screen asks: <em>{'"Please confirm the Office/Team you are with. If you do not see your Office/Team, please reach out to the Operations Team."'}</em></>,
    ]],
    note: 'There is no Household picker on Salesforce requests.',
  }),
  {
    title: 'Say whether this replaces someone',
    body: (
      <p style={{ margin: 0 }}>
        The <strong>New User License Details</strong> screen opens with one question: is this replacing an
        existing user, or an entirely new license? If it is a replacement, name the user being replaced. The
        new user&rsquo;s details appear once that is answered.
      </p>
    ),
  },
  {
    title: "Enter the new user's details",
    body: (
      <p style={{ margin: 0 }}>
        First and last name, phone, email, role, manager and an Additional Details box - all required.
      </p>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice at the foot of the screen, select <em>I have read, understand,
        and agree to the above information</em> under Acknowledgement and click Next. FSC creates the case and
        opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

const QUESTIONS = [
  {
    q: 'Is this to replace an existing User license or a new license entirely?',
    when: 'Always - this is the first question',
    options: ['Yes (This is to replace an existing User)', 'No (This is an entirely new one)'],
  },
  {
    q: 'Who is the User who is being exchanged/replaced?',
    when: 'Only if you chose Yes (This is to replace an existing User)',
    note: 'The rest of the screen stays hidden until this is filled in.',
  },
  {
    q: 'New User First Name',
    when: 'If you chose No (This is an entirely new one), or once the replaced user is named',
  },
  { q: 'New User Last Name', when: 'With the first name' },
  { q: 'Phone', when: 'With the first name' },
  { q: 'Email', when: 'With the first name', note: 'Must be a valid email address.' },
  { q: 'New User Role', when: 'With the first name', note: 'Free text - describe the job they do.' },
  { q: "New User's Manager", when: 'With the first name' },
  {
    q: 'Additional Details',
    when: 'With the first name',
    note: 'Required. This becomes the case description Operations reads first - add anything else they need, such as a start date or which access the user should match.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Salesforce'),
  {
    h: "My Office/Team isn't in the Office picker",
    p: ['The screen asks you to reach out to the Operations Team if you do not see your Office/Team.'],
  },
  {
    h: "The new user's details haven't appeared",
    p: ['Answer the first question. If you chose Yes (replacing an existing user), the details also wait until the name of the user being replaced is filled in.'],
  },
];

export default function SalesforceNewLicense() {
  return (
    <GuidePage
      title="Salesforce License Requests"
      icon="person-plus"
      path="Operations → Salesforce → New License"
      minutes={3}
      intro="How to ask the Prosperity Operations team for a Salesforce user license, new or replacing someone - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. Every field on the screen is required.</Para>
        <Bullets items={[
          <><strong>Whether it replaces someone</strong>, and if so, who.</>,
          <><strong>The new user&rsquo;s first and last name, phone and email.</strong></>,
          <><strong>Their role and their manager.</strong></>,
          <><strong>Anything else Operations should know</strong>, for the Additional Details box.</>,
        ]} />
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to gather the details before you start, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="operations"
        confirmations={false}
        rows={[]}
        before={
          <Para>
            FSC creates the case, shares it with you and your office, and opens it. Your answers are written onto
            the case, and Additional Details becomes its description.
          </Para>
        }
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. Submitting it asks Operations for the license; it does not
        create the user in Salesforce by itself.
      </Footnote>
    </GuidePage>
  );
}
